import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { transferApi, warehouseApi } from '../services/api';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  Building2, Calendar, FileText, ArrowLeftRight, Check, X,
  Clock, CheckCircle2, AlertCircle, FileCheck2,
  RefreshCw, Send, AlertTriangle, Search, Download, Truck,
  Boxes, User, ChevronRight, ArrowRight,
  CircleDollarSign, FileInput, Leaf, Loader2, Hash, Target, Eye
} from 'lucide-react';

const { RangePicker } = DatePicker;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
  } catch {
    return dateStr;
  }
};

const getMockHsn = (productName = '') => {
  const name = productName.toLowerCase();
  if (name.includes('capsule') || name.includes('vati') || name.includes('tablet') || name.includes('churna')) {
    return '300490';
  } else if (name.includes('juice') || name.includes('gel')) {
    return '220299';
  } else if (name.includes('oil') || name.includes('taila')) {
    return '151590';
  }
  return '300490';
};

const getMockUnit = (productName = '') => {
  const name = productName.toLowerCase();
  if (name.includes('capsule') || name.includes('tablet') || name.includes('vati')) {
    return 'Bot';
  } else if (name.includes('churna') || name.includes('awaleha') || name.includes('powder')) {
    return 'Jar';
  } else if (name.includes('juice') || name.includes('gel') || name.includes('liquid')) {
    return 'Bot';
  }
  return 'Bot';
};

const getMockExpiry = (dateStr) => {
  if (dateStr) return dateStr;
  return '12/2028';
};

const getWarehouseState = (warehouseName = '') => {
  const name = warehouseName.toLowerCase();
  if (name.includes('noida') || name.includes('vrindavan') || name.includes('bulandshahr')) {
    return 'Uttar Pradesh';
  }
  if (name.includes('delhi')) {
    return 'Delhi';
  }
  return 'Uttar Pradesh'; // Default
};

const getItemPrice = (productName = '') => {
  const name = productName.toLowerCase();
  if (name.includes('capsule') || name.includes('vati') || name.includes('tablet')) {
    return 250;
  }
  if (name.includes('juice') || name.includes('gel')) {
    return 150;
  }
  if (name.includes('churna') || name.includes('awaleha')) {
    return 300;
  }
  return 200; // Default price
};

const formatRupees = (amount) => {
  return '₹' + Number(amount).toLocaleString('en-IN');
};

function StockTransfer() {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme?.name);

  // View state: 'list' | 'form' | 'receive'
  const [viewMode, setViewMode] = useState('list');

  // ── WAREHOUSES (API) ──
  const [warehousesList, setWarehousesList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const w = await warehouseApi.getActiveWarehouses();
        if (!cancelled) setWarehousesList(Array.isArray(w) ? w : (w?.content || w?.data || []));
      } catch (err) {
        console.error('Warehouse load error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const warehouseName = (id) => warehousesList.find(w => String(w.id) === String(id))?.name || '';

  // Form states — transferFrom / transferTo now hold warehouse IDs
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
  const [priority, setPriority] = useState('Normal');
  const [notes, setNotes] = useState('');
  const [dateFocused, setDateFocused] = useState(false);
  const [creating, setCreating] = useState(false);

  const [vehicleNumber, setVehicleNumber] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [transferToName, setTransferToName] = useState('');
  const [referenceId, setReferenceId] = useState('');

  useEffect(() => {
    if (viewMode === 'form' && !referenceId) {
      const yy = new Date().getFullYear().toString().slice(-2);
      const mm = (new Date().getMonth() + 1).toString().padStart(2, '0');
      const rand = Math.floor(10 + Math.random() * 90);
      setReferenceId(`TR-${yy}${mm}-00${rand}`);
    }
  }, [viewMode, referenceId]);

  // Toast Popup State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Stock transfer request created successfully!');

  const [activeDropdownId, setActiveDropdownId] = useState(null);

  // Document view states
  const [selectedDocTransfer, setSelectedDocTransfer] = useState(null);
  const [docType, setDocType] = useState('challan'); // 'challan' | 'invoice' | 'eway'
  const [docLoading, setDocLoading] = useState(false);
  const [docDownloading, setDocDownloading] = useState(false);

  const viewDocument = async (id, type) => {
    setDocLoading(true);
    setViewMode('document');
    setDocType(type);
    try {
      const detail = await transferApi.getTransfer(id);
      setSelectedDocTransfer(detail);
    } catch (err) {
      alert(err.message || 'Failed to load transfer details');
      setViewMode('list');
    } finally {
      setDocLoading(false);
    }
  };

  // ── Download a document (Delivery Challan / Invoice / E-way Bill) as a real backend-generated PDF ──
  const handleDownloadDoc = async (transferId, type) => {
    setDocDownloading(true);
    try {
      const blob = type === 'challan'
        ? await transferApi.downloadDeliveryChallanPdf(transferId)
        : type === 'invoice'
          ? await transferApi.downloadInvoicePdf(transferId)
          : await transferApi.downloadEwayBillPdf(transferId);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${transferId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message || `Failed to download ${type}`);
    } finally {
      setDocDownloading(false);
    }
  };

  // ── PRODUCTS AVAILABLE IN SOURCE WAREHOUSE (API) ──
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (!transferFrom) { setProducts([]); return; }
    let cancelled = false;
    (async () => {
      setProductsLoading(true);
      try {
        const stock = await warehouseApi.getWarehouseStock(transferFrom);
        if (cancelled) return;
        const list = stock?.content || stock?.data || stock || [];
        setProducts((Array.isArray(list) ? list : []).map(p => ({
          id: p.productId ?? p.id,
          name: p.productName || p.name || '',
          sku: p.sku || '',
          available: p.availableQty ?? p.quantity ?? 0,
          transferQty: '',
          batchNo: p.batchNumber || p.batchNo || '',
          expiryDate: p.expiryDate || '',
          selected: false,
        })));
      } catch (err) {
        console.error('Warehouse stock load error:', err);
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [transferFrom]);

  // ── TRANSFERS LIST (API) ──
  const [transfers, setTransfers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Filter States for List View
  const [listSearch, setListSearch] = useState('');
  const [listFrom, setListFrom] = useState('All');
  const [listTo, setListTo] = useState('All');
  const [listDateFrom, setListDateFrom] = useState('');
  const [listDateTo, setListDateTo] = useState('');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(listSearch), 400);
    return () => clearTimeout(t);
  }, [listSearch]);

  const mapTransfer = (t, idx) => {
    const initials = (name = '') => name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '—';
    return {
      id: t.id ?? idx,
      transferId: t.transferNumber || t.transferId || t.referenceId || `TR-${t.id}`,
      source: t.sourceWarehouseName || t.source || '',
      destination: t.destinationWarehouseName || t.destination || '',
      products: t.itemsCount != null
        ? `${t.itemsCount} SKU${t.itemsCount !== 1 ? 's' : ''}`
        : t.totalProducts != null
          ? `${t.totalProducts} SKU${t.totalProducts !== 1 ? 's' : ''}`
          : (t.products || ''),
      qty: String(t.totalQty ?? t.totalQuantity ?? t.qty ?? 0),
      status: t.status === 'IN_TRANSIT' ? 'In Transit'
        : t.status === 'COMPLETED' ? 'Completed'
        : (t.status || 'In Transit'),
      date: t.transferDate || (t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      user: t.createdByName || t.user || '',
      initials: initials(t.createdByName || t.user),
      // 🔧 FIXED: API doesn't send a top-level "varianceQty" — it sends
      // "hasVariance" (boolean) and "shortfallUnits" (total missing units)
      // at the transfer level. Per-item varianceQty only exists inside
      // items[], which the list view doesn't have. Use the real fields.
      hasVariance: t.hasVariance ?? false,
      shortfallUnits: t.shortfallUnits ?? 0,
    };
  };

  const loadTransfers = async () => {
    setListLoading(true);
    setListError(null);
      try {
        const res = await transferApi.searchTransfers({
          searchTerm: debouncedSearch,
          startDate: listDateFrom,
          endDate: listDateTo,
        });
        // 🔧 FIXED: backend response shape is { transfers: [...], totalEntries, ... } —
        // not { content: [...] } or { data: [...] }. The old fallback chain
        // (res?.content || res?.data || res) landed on the raw object (not an
        // array) whenever neither `content` nor `data` existed, so
        // Array.isArray(list) was always false and the table stayed empty even
        // though the API was returning real data.
        const list = res?.transfers || res?.content || res?.data || res || [];
        setTransfers(Array.isArray(list) ? list.map(mapTransfer) : []);
      } catch (err) {
      setListError(err.message || 'Failed to load transfers');
      console.error('Transfers load error:', err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, listDateFrom, listDateTo]);

  // Update a product's transfer quantity inside form
  const handleQtyChange = (id, qty) => {
    setProducts(products.map(p => {
      if (p.id === id) {
        const cleanQty = qty === '' ? '' : Math.max(0, parseInt(qty) || 0);
        return {
          ...p,
          transferQty: cleanQty === '' ? '' : String(cleanQty),
          selected: cleanQty !== '' ? true : p.selected
        };
      }
      return p;
    }));
  };

  const toggleSelect = (id) => {
    setProducts(products.map(p => {
      if (p.id === id) {
        const nextSel = !p.selected;
        return { ...p, selected: nextSel, transferQty: nextSel ? '0' : '' };
      }
      return p;
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = products.length > 0 && products.every(p => p.selected);
    setProducts(products.map(p => ({ ...p, selected: !allSelected, transferQty: !allSelected ? '0' : '' })));
  };

  // Checklist Validation Statuses inside form
  const isSourceSet = transferFrom !== '';
  const isDestSet = transferTo !== '';

  const selectedProducts = products.filter(p => p.selected && p.transferQty !== '' && parseInt(p.transferQty) > 0);
  const isAtLeastOneProduct = selectedProducts.length > 0;

  const isAllQuantitiesEntered = products.filter(p => p.selected).length > 0 && products.filter(p => p.selected).every(p => {
    const qtyVal = parseInt(p.transferQty);
    return !isNaN(qtyVal) && qtyVal >= 0;
  });

  const isNoQtyExceeded = products.filter(p => p.selected).every(p => {
    const qtyVal = parseInt(p.transferQty) || 0;
    return qtyVal <= p.available;
  });

  const selectedSKUsCount = products.filter(p => p.selected && p.transferQty !== '' && parseInt(p.transferQty) >= 0).length;
  const totalUnits = products.filter(p => p.selected).reduce((sum, p) => sum + (parseInt(p.transferQty) || 0), 0);
  const isFormValid = isSourceSet && isDestSet && transferFrom !== transferTo && isAtLeastOneProduct && isAllQuantitiesEntered && isNoQtyExceeded;

  const resetForm = () => {
    setTransferFrom('');
    setTransferTo('');
    setTransferDate(new Date().toISOString().slice(0, 10));
    setPriority('Normal');
    setNotes('');
    setProducts([]);
    setVehicleNumber('');
    setTransporterName('');
    setTransferToName('');
    setReferenceId('');
  };

  // ── Handle Save Submission (API) ──
  const handleCreateTransfer = async () => {
    if (!isSourceSet || !isDestSet) {
      alert('Please select both source and destination warehouses.');
      return;
    }
    if (transferFrom === transferTo) {
      alert('Source and Destination warehouses cannot be the same.');
      return;
    }
    if (!isAtLeastOneProduct) {
      alert('Please enter a transfer quantity for at least one product.');
      return;
    }
    if (!isNoQtyExceeded) {
      alert('One or more transfer quantities exceed the available warehouse stock.');
      return;
    }

    setCreating(true);
    try {
      await transferApi.createTransfer({
        sourceWarehouseId: Number(transferFrom),
        destinationWarehouseId: Number(transferTo),
        transferDate,
        priority: priority.toUpperCase(),
        notes,
        createdByName: 'Ramesh K.', // TODO: replace with logged-in user's name
        vehicleNumber,
        transporterName,
        transferToName,
        referenceId,
        items: selectedProducts.map(p => ({
          productId: p.id,
          batchNumber: p.batchNo,
          transferQty: parseInt(p.transferQty) || 0,
        })),
      });

      setToastMessage('Stock transfer request created successfully!');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setViewMode('list');
        resetForm();
        loadTransfers();
      }, 1500);
    } catch (err) {
      alert(err.message || 'Failed to create transfer');
    } finally {
      setCreating(false);
    }
  };

  // ── RECEIVE TRANSFER FLOW (API) ──
  const [pendingQueue, setPendingQueue] = useState([]);
  const [pendingQueueLoading, setPendingQueueLoading] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null); // full transfer detail
  const [receiveItems, setReceiveItems] = useState([]);
  const [receivingNotes, setReceivingNotes] = useState('');
  const [confirmingReceive, setConfirmingReceive] = useState(false);

  const loadPendingQueue = async () => {
    setPendingQueueLoading(true);
    try {
      const res = await transferApi.getPendingQueue();
      // 🔧 FIXED: same shape issue as loadTransfers — backend wraps the array
      // as { transfers: [...] } (or similar), not { content }/{ data }.
      const list = res?.transfers || res?.content || res?.data || res || [];
      setPendingQueue(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Pending queue load error:', err);
      setPendingQueue([]);
    } finally {
      setPendingQueueLoading(false);
    }
  };

  const openReceiveScreen = () => {
    setSelectedTransfer(null);
    setReceiveItems([]);
    setReceivingNotes('');
    setViewMode('receive');
    loadPendingQueue();
  };

  const selectTransferToReceive = async (transferSummary) => {
    try {
      const detail = await transferApi.getTransfer(transferSummary.id);
      setSelectedTransfer(detail);
      const items = detail.items || detail.transferItems || [];
      setReceiveItems(items.map(it => ({
        id: it.id ?? it.transferItemId,
        name: it.productName || it.name || '',
        sku: it.sku || '',
        expectedQty: it.transferQty ?? it.expectedQty ?? 0,
        receivedQty: it.transferQty ?? it.expectedQty ?? 0,
        batchNo: it.batchNumber || it.batchNo || '',
      })));
    } catch (err) {
      alert(err.message || 'Failed to load transfer details');
    }
  };

  const handleReceiveQtyChange = (id, val) => {
    const updated = receiveItems.map(item => item.id === id ? { ...item, receivedQty: val } : item);
    setReceiveItems(updated);

    const diffs = updated.filter(i => i.receivedQty !== i.expectedQty);
    if (diffs.length > 0) {
      const notesStr = diffs.map(i => {
        const diff = i.expectedQty - i.receivedQty;
        return `${diff} units missing in ${i.name.toLowerCase()}`;
      }).join('\n');
      setReceivingNotes(notesStr);
    } else {
      setReceivingNotes('');
    }
  };

  const totalExpected = receiveItems.reduce((sum, item) => sum + item.expectedQty, 0);
  const totalReceived = receiveItems.reduce((sum, item) => sum + item.receivedQty, 0);
  const discrepantItems = receiveItems.filter(item => item.receivedQty !== item.expectedQty);
  const hasVariance = discrepantItems.length > 0;

  const handleConfirmReceive = async () => {
    if (!selectedTransfer) return;
    setConfirmingReceive(true);
    try {
      await transferApi.receiveTransfer(selectedTransfer.id, {
        items: receiveItems.map(item => ({
          transferItemId: item.id,
          receivedQty: item.receivedQty,
        })),
        receivingNotes,
        receivedByName: 'Meenal Joshi', // TODO: replace with logged-in user's name
      });

      setToastMessage('Transfer received successfully!');
      setShowToast(true);
      setViewMode('list');
      loadTransfers();
      setTimeout(() => setShowToast(false), 3500);
    } catch (err) {
      alert(err.message || 'Failed to confirm receive');
    } finally {
      setConfirmingReceive(false);
    }
  };

  // ── PDF/Excel export (API) — bulk transfer list report, same search/date filters as the list ──
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        startDate: listDateFrom || undefined,
        endDate: listDateTo || undefined,
      };

      const blob = format === 'pdf'
        ? await transferApi.exportTransfersPdf(params)
        : await transferApi.exportTransfersExcel(params);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transfers-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message || `Failed to export ${format}`);
    } finally {
      setExporting(false);
    }
  };

  // Filter transfers for listing view (client-side; server search already applied on text/date)
  const filteredTransfers = transfers.filter(t => {
    const matchesFrom = listFrom === 'All' || t.source === listFrom;
    const matchesTo = listTo === 'All' || t.destination === listTo;
    return matchesFrom && matchesTo;
  });

  const inTransitCount = transfers.filter(t => t.status === 'In Transit').length;
  const completedCount = transfers.filter(t => t.status === 'Completed').length;
  const totalSkuMovementUnits = transfers.reduce((sum, t) => sum + (parseInt(t.qty) || 0), 0);

  return (
    <div className="space-y-6 pb-28 relative">
      {/* ── SUCCESS TOAST POPUP ── */}
      {showToast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[10000] transition-all duration-300">
          <div className="flex items-center gap-3 bg-[#eefdf5] border border-emerald-500/30 px-4 py-3 rounded-xl shadow-lg">
            <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 stroke-[3]" />
            </div>
            <span className="text-sm font-bold text-gray-800">{toastMessage}</span>
            <button
              onClick={() => setShowToast(false)}
              className="w-5 h-5 rounded-full border border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-50 transition-all ml-4 cursor-pointer"
            >
              <X className="w-3.5 h-3.5 stroke-[3]" />
            </button>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <>
          {/* ── HEADER ── */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Transfer Entries
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-550'} mt-1`}>
                {listLoading ? 'Loading...' : `${filteredTransfers.length} total entries · All warehouses`}
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transfer ID, warehouse, user..."
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  className={`pl-10 pr-4 py-2.5 rounded-xl text-xs w-full outline-none border focus:border-emerald-500 transition-all ${
                    isDark
                      ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                      : 'bg-white border-gray-200 text-gray-850 placeholder-gray-400 shadow-sm'
                  }`}
                />
              </div>

              <button
                onClick={openReceiveScreen}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-sm cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: currentTheme.color }}
              >
                <FileInput className="w-4 h-4" />
                Receive Transfer
              </button>

              <button
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
                }`}
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-500" /> : <FileText className="w-3.5 h-3.5 text-rose-500" />}
                PDF
              </button>

              <button
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
                }`}
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> : <Download className="w-3.5 h-3.5 text-emerald-600" />}
                Export Excel
              </button>

              <button
                onClick={() => setViewMode('form')}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-md cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: currentTheme.color }}
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                New Transfer
              </button>
            </div>
          </div>

          {/* ── ERROR BANNER ── */}
          {listError && (
            <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{listError}</span>
              <button onClick={loadTransfers} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
                Retry
              </button>
            </div>
          )}

          {/* ── METRICS GRID ROW ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/20 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Intransit Transfers</p>
                <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-955'}`}>
                  {listLoading ? '—' : String(inTransitCount).padStart(2, '0')}
                </p>
              </div>
            </div>

            <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-455 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Completed</p>
                <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-955'}`}>
                  {listLoading ? '—' : String(completedCount).padStart(2, '0')}
                </p>
              </div>
            </div>

            <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isDark ? 'bg-purple-950/30 text-purple-400' : 'bg-purple-50 text-purple-600'
              }`}>
                <Boxes className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total SKU Movement</p>
                <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-955'}`}>
                  {listLoading ? '—' : `${totalSkuMovementUnits.toLocaleString('en-IN')} Units`}
                </p>
              </div>
            </div>
          </div>

          {/* ── FILTERS PANEL CARD ── */}
          <div className={`p-4 rounded-xl border flex flex-wrap items-center gap-4 ${
            isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <select
              value={listFrom}
              onChange={(e) => setListFrom(e.target.value)}
              className={`px-3 py-2 rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
              }`}
            >
              <option value="All">From (any)</option>
              {warehousesList.map(wh => (
                <option key={wh.id} value={wh.name}>{wh.name}</option>
              ))}
            </select>

            <select
              value={listTo}
              onChange={(e) => setListTo(e.target.value)}
              className={`px-3 py-2 rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
              }`}
            >
              <option value="All">To (any)</option>
              {warehousesList.map(wh => (
                <option key={wh.id} value={wh.name}>{wh.name}</option>
              ))}
            </select>

            <div className="flex items-center">
              <RangePicker
                value={
                  listDateFrom && listDateTo
                    ? [dayjs(listDateFrom), dayjs(listDateTo)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setListDateFrom(dates[0].format('YYYY-MM-DD'));
                    setListDateTo(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setListDateFrom('');
                    setListDateTo('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ borderRadius: '12px', padding: '6px 12px' }}
                className="text-xs"
                size="middle"
              />
            </div>
          </div>

          {/* ── TRANSFERS TABLE CARD ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-800/50 text-[10px] font-black uppercase tracking-wider text-emerald-800 dark:text-emerald-505 pb-3">
                    <th className="pb-3 pl-2">Transfer ID</th>
                    <th className="pb-3">Source Warehouse</th>
                    <th className="pb-3">Destination</th>
                    <th className="pb-3">Products</th>
                    <th className="pb-3 text-center">Quantity</th>
                    <th className="pb-3 text-center">STATUS</th>
                    <th className="pb-3">Date</th>
                    <th className="pb-3 pr-2 text-center">Created By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40 text-xs">
                  {listLoading ? (
                    <tr>
                      <td colSpan="8" className="py-12 text-center">
                        <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                        <span className="text-gray-400 text-sm">Loading transfers...</span>
                      </td>
                    </tr>
                  ) : filteredTransfers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-gray-400 text-sm font-semibold">
                        No transfers found matching the active filters.
                      </td>
                    </tr>
                  ) : (
                    filteredTransfers.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50/20 dark:hover:bg-slate-900/5 transition-colors">
                        <td className="py-4 pl-2 font-black text-emerald-650 dark:text-emerald-400">
                          {t.transferId}
                        </td>
                        <td className="py-4 font-bold text-gray-700 dark:text-slate-300">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-500" />
                            <span>{t.source}</span>
                          </div>
                        </td>
                        <td className="py-4 font-bold text-gray-700 dark:text-slate-300">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-emerald-555" />
                            <span>{t.destination}</span>
                          </div>
                        </td>
                        <td className="py-4 text-gray-550 dark:text-slate-400 font-medium">
                          {t.products}
                        </td>
                        <td className="py-4 text-center font-black text-emerald-600 dark:text-emerald-450">
                          {t.qty} <span className="text-[10px] font-normal text-gray-400">units</span>
                        </td>
                        <td className="py-4 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold ${
                              t.status === 'Completed'
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-450'
                                : 'bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'Completed' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                              {t.status}
                            </span>
                            {/* 🔧 CHANGED: was hardcoded "5 units short fall" for every
                                Completed row. Now driven by the real hasVariance +
                                shortfallUnits fields from the API — only shown when
                                the transfer actually had a shortfall on receiving. */}
                            {t.status === 'Completed' && t.hasVariance && t.shortfallUnits > 0 && (
                              <span className="text-red-500 text-[11px] font-semibold mt-1 whitespace-nowrap">
                                {t.shortfallUnits} units short fall
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 text-gray-500 dark:text-slate-400 font-bold">
                          {t.date}
                        </td>
                        <td className="py-4 pr-2 relative">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center font-black text-[9px]">
                                {t.initials}
                              </span>
                              <span className={`font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{t.user}</span>
                            </div>

                            <div className="relative flex items-center">
                              <button
                                onClick={() => setActiveDropdownId(activeDropdownId === t.id ? null : t.id)}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-emerald-600 dark:text-emerald-400 cursor-pointer"
                              >
                                <Eye className="w-4.5 h-4.5" />
                              </button>

                              {activeDropdownId === t.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setActiveDropdownId(null)}
                                  />
                                  <div className={`absolute right-8 top-1/2 -translate-y-1/2 mr-2 w-36 rounded-xl border shadow-xl z-50 py-1.5 text-xs font-semibold ${
                                    isDark
                                      ? 'bg-slate-900 border-slate-800 text-slate-200 shadow-slate-950/50'
                                      : 'bg-white border-gray-200 text-gray-750'
                                  }`}>
                                    {/* Small arrow triangle pointing to the eye */}
                                    <div className={`absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 rotate-45 border-t border-r ${
                                      isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
                                    }`} />

                                    <div className="relative z-10 bg-inherit rounded-xl">
                                      <button
                                        onClick={() => {
                                          setActiveDropdownId(null);
                                          viewDocument(t.id, 'challan');
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                      >
                                        Delivery challan
                                      </button>
                                      <button
                                        onClick={() => {
                                          setActiveDropdownId(null);
                                          viewDocument(t.id, 'invoice');
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-t border-gray-100 dark:border-slate-800/40"
                                      >
                                        Invoice
                                      </button>
                                      <button
                                        onClick={() => {
                                          setActiveDropdownId(null);
                                          viewDocument(t.id, 'eway');
                                        }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-t border-gray-100 dark:border-slate-800/40"
                                      >
                                        E-way bill
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100 dark:border-slate-800/40 text-xs font-bold text-gray-400">
              <span>{filteredTransfers.length} entries</span>
            </div>
          </div>
        </>
      )}

      {viewMode === 'form' && (
        <>
          {/* ── NEW STOCK TRANSFER FORM VIEW ── */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Stock Transfer
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-555'} mt-1`}>
                Move inventory between warehouses — changes update stock levels immediately
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => { setViewMode('list'); resetForm(); }}
                className={`flex items-center gap-1 px-4 py-2 border rounded-xl text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer ${
                  isDark ? 'border-slate-800 text-gray-300' : 'border-gray-202 text-gray-655 shadow-sm'
                }`}
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                Back to List
              </button>
            </div>
          </div>

          <div className="space-y-6">

            {/* CARD 1: TRANSFER DETAILS */}
            <div className={`border rounded-2xl p-6 transition-all ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Transfer Details
                  </h3>
                  <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-550'} font-medium mt-0.5`}>
                    Source, destination and scheduling
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Source warehouse — value is warehouse ID */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    TRANSFER FROM <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={transferFrom}
                      onChange={(e) => setTransferFrom(e.target.value)}
                      className={`pl-10 pr-10 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                        isDark
                          ? 'bg-slate-955 border-slate-800 text-white'
                          : `bg-white border-gray-200 ${transferFrom === '' ? 'text-gray-405' : 'text-gray-800'}`
                      }`}
                    >
                      <option value="">Select</option>
                      {warehousesList.map(wh => (
                        <option key={wh.id} value={wh.id}>{wh.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Destination warehouse — value is warehouse ID */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    TRANSFER TO <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={transferTo}
                        onChange={(e) => setTransferTo(e.target.value)}
                        className={`pl-10 pr-10 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                          isDark
                            ? 'bg-slate-955 border-slate-800 text-white'
                            : `bg-white border-gray-200 ${transferTo === '' ? 'text-gray-405' : 'text-gray-800'}`
                        }`}
                      >
                        <option value="">Select</option>
                        {warehousesList.map(wh => (
                          <option key={wh.id} value={wh.id}>{wh.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="relative flex-1">
                      <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={transferToName}
                        onChange={(e) => setTransferToName(e.target.value)}
                        placeholder="Enter name"
                        className={`pl-10 pr-4 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                          isDark
                            ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                            : 'bg-white border-gray-200 text-gray-850 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* TRANSFER DATE */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    TRANSFER DATE <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type={dateFocused || transferDate ? 'date' : 'text'}
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                      onFocus={() => setDateFocused(true)}
                      onBlur={() => setDateFocused(false)}
                      placeholder="Select date"
                      className={`pl-10 pr-10 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                        isDark
                          ? 'bg-slate-900 border-slate-800 text-white'
                          : `bg-white border-gray-200 ${transferDate === '' ? 'text-gray-400' : 'text-gray-855'}`
                      }`}
                    />
                  </div>
                </div>

                {/* TRANSFER REFERENCE ID */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    TRANSFER REFERENCE ID
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <div className={`pl-10 pr-4 py-2.5 rounded-2xl border text-sm w-full font-bold flex items-center justify-between min-h-[42px] ${
                      isDark
                        ? 'bg-slate-900 border-slate-800 text-emerald-400'
                        : 'bg-emerald-50/10 border-gray-200 text-emerald-750'
                    }`}>
                      <span>{referenceId}</span>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                        Auto
                      </span>
                    </div>
                  </div>
                </div>

                {/* PRIORITY */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    PRIORITY
                  </label>
                  <div className="relative">
                    <Target className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className={`pl-10 pr-10 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                        isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-850'
                      }`}
                    >
                      <option value="Low">Low</option>
                      <option value="Normal">Normal</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                </div>

                {/* VEHICLE NUMBER */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    VEHICLE NUMBER
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={vehicleNumber}
                      onChange={(e) => setVehicleNumber(e.target.value)}
                      placeholder="Please enter"
                      className={`pl-10 pr-4 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                        isDark
                          ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                          : 'bg-white border-gray-200 text-gray-850 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>

                {/* TRANSPORTER NAME */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-[#459b7a] block">
                    TRANSPORTER NAME
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={transporterName}
                      onChange={(e) => setTransporterName(e.target.value)}
                      placeholder="Please enter"
                      className={`pl-10 pr-4 py-2.5 rounded-2xl border text-sm w-full outline-none focus:border-[#459b7a] focus:ring-1 focus:ring-[#459b7a]/20 transition-all ${
                        isDark
                          ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                          : 'bg-white border-gray-200 text-gray-855 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>

                {/* Spacer to align grid correctly */}
                <div className="hidden md:block"></div>
              </div>

              {/* Badges summarizing selected warehouses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <div className={`p-4 rounded-xl border flex items-center gap-4 ${
                  isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-blue-50/10 border-blue-100'
                }`}>
                  <div className="w-10 h-10 rounded-full bg-[#5c7099] text-white flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-wider text-[#3b82f6] leading-none mb-1">FROM WAREHOUSE</p>
                    <p className={`text-xs font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {transferFrom ? warehouseName(transferFrom) : 'Not selected'}
                    </p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                      {transferFrom ? `${products.length} SKUs available` : '—'}
                    </p>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border flex items-center gap-4 ${
                  isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-emerald-50/10 border-emerald-100'
                }`}>
                  <div className="w-10 h-10 rounded-full bg-[#4d7c65] text-white flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-wider text-[#10b981] leading-none mb-1">TO WAREHOUSE</p>
                    <p className={`text-xs font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {transferTo ? warehouseName(transferTo) : 'Not selected'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 2: SELECT PRODUCTS TO TRANSFER */}
            <div className={`border rounded-2xl p-6 transition-all ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
                  <Boxes className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Select Products to Transfer
                  </h3>
                  <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-550'} font-medium`}>
                    Choose source warehouse to load available stock
                  </p>
                </div>
              </div>

              {productsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mr-2" />
                  <span className="text-gray-400 text-sm">Loading stock...</span>
                </div>
              ) : transferFrom ? (
                products.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <h4 className="text-sm font-bold text-gray-700 dark:text-slate-350">No stock available</h4>
                    <p className="text-xs text-gray-400 mt-1">This warehouse has no available stock to transfer.</p>
                  </div>
                ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-slate-800/50 text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500 pb-3">
                        <th className="pb-3 w-10 text-center">
                          <div
                            onClick={toggleSelectAll}
                            className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-all ${
                              products.every(p => p.selected)
                                ? 'border-emerald-500 bg-emerald-500 text-white'
                                : isDark ? 'border-slate-800 bg-slate-955/40' : 'border-gray-300 bg-white'
                            }`}
                          >
                            {products.every(p => p.selected) && <Check className="w-3 h-3 stroke-[3]" />}
                          </div>
                        </th>
                        <th className="pb-3 pl-2">Product Name</th>
                        <th className="pb-3 text-center">Available</th>
                        <th className="pb-3 text-center">Transfer Qty</th>
                        <th className="pb-3 text-center">Batch No.</th>
                        <th className="pb-3 text-center">Expiry Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40 text-xs">
                      {products.map(p => {
                        const isQtyExceeded = p.selected && parseInt(p.transferQty) > p.available;
                        return (
                          <tr key={p.id} className={`hover:bg-slate-50/20 dark:hover:bg-slate-900/5 ${p.selected ? 'bg-emerald-50/5 dark:bg-emerald-950/5' : ''}`}>
                            <td className="py-4 text-center">
                              <div
                                onClick={() => toggleSelect(p.id)}
                                className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-all mx-auto ${
                                  p.selected
                                    ? 'border-emerald-500 bg-emerald-500 text-white'
                                    : isDark ? 'border-slate-800 bg-slate-955/40' : 'border-gray-300 bg-white'
                                }`}
                              >
                                {p.selected && <Check className="w-3 h-3 stroke-[3]" />}
                              </div>
                            </td>
                            <td className="py-4 pl-2 pr-4">
                              <p className={`text-xs font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{p.name}</p>
                              <p className="text-[10px] text-gray-400 font-mono mt-0.5">{p.sku}</p>
                            </td>
                            <td className="py-4 text-center text-xs font-black text-emerald-600 dark:text-emerald-450 whitespace-nowrap">
                              {p.available.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">units</span>
                            </td>
                            <td className="py-4 px-2 w-36">
                              {p.selected ? (
                                <div className="space-y-1">
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={p.transferQty}
                                    onChange={(e) => handleQtyChange(p.id, e.target.value)}
                                    className={`px-3 py-1.5 rounded-xl border text-sm w-full text-center outline-none focus:ring-1 transition-all ${
                                      isQtyExceeded
                                        ? 'border-rose-500 bg-rose-50/5 dark:bg-rose-950/5 focus:ring-rose-500'
                                        : 'border-emerald-500 focus:ring-emerald-500 bg-emerald-50/5'
                                    }`}
                                  />
                                  {isQtyExceeded && (
                                    <p className="text-[9px] font-black text-rose-500 text-center leading-none">Exceeds available</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 block text-center font-bold">—</span>
                              )}
                            </td>
                            <td className="py-4 text-center text-xs font-mono font-bold text-gray-650 dark:text-slate-400">
                              {p.batchNo}
                            </td>
                            <td className="py-4 text-center text-xs font-semibold text-gray-550 dark:text-slate-400">
                              {p.expiryDate}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/20 text-[#459b7a] flex items-center justify-center mb-3">
                    <Building2 className="w-7 h-7" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-700 dark:text-slate-350">No warehouse selected</h4>
                  <p className="text-xs text-gray-400 mt-1 max-w-[280px]">
                    Select a source warehouse above to view available stock
                  </p>
                </div>
              )}
            </div>

            {/* CARD 3: TRANSFER NOTES */}
            <div className={`border rounded-2xl p-6 transition-all ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Transfer Notes
                  </h3>
                  <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-550'} font-medium`}>
                    Special instructions for the warehouse team
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <textarea
                  maxLength={500}
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`w-full p-4 rounded-xl border text-sm outline-none focus:border-emerald-500 transition-all resize-none ${
                    isDark
                      ? 'bg-slate-955 border-slate-800 text-white placeholder-slate-500'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 shadow-sm'
                  }`}
                  placeholder="e.g. Handle with care — temperature-sensitive products. Verify batch numbers upon receipt..."
                />
                <div className="flex justify-between items-center text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                  <span>Optional — visible to receiving warehouse</span>
                  <span>{notes.length} / 500</span>
                </div>
              </div>
            </div>

            {/* CARD 4: TRANSFER SUMMARY */}
            <div className={`border rounded-2xl p-6 transition-all ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
                  <FileCheck2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Transfer Summary
                  </h3>
                  <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
                    Validate checklist and overview details
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-3.5 text-xs font-semibold">
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">From</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {transferFrom ? warehouseName(transferFrom) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">To</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {transferTo ? warehouseName(transferTo) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">Transfer Date</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{transferDate}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">Products Selected</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedSKUsCount} SKU{selectedSKUsCount !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">Vehicle Number</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {vehicleNumber || '—'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-150 dark:border-slate-800/50 pb-2">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">Transporter Name</span>
                    <span className={`font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {transporterName || '—'}
                    </span>
                  </div>
                  <div className="flex justify-between pb-1">
                    <span className="uppercase tracking-wider text-gray-500 dark:text-slate-400">Total Units</span>
                    <span className={`font-black text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{totalUnits}</span>
                  </div>
                </div>

                <div className="space-y-2.5 pt-4 border-t border-gray-100 dark:border-slate-800/40">
                  {[
                    { label: 'Source warehouse set', ok: isSourceSet },
                    { label: 'Destination set', ok: isDestSet },
                    { label: 'At least one product', ok: isAtLeastOneProduct },
                    { label: 'All quantities entered', ok: isAllQuantitiesEntered },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      {item.ok ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-slate-700 flex items-center justify-center text-gray-400 font-black text-[10px]">•</div>
                      )}
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${item.ok ? 'text-emerald-700 dark:text-emerald-500' : 'text-gray-400'}`}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    {isNoQtyExceeded ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />
                    )}
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isNoQtyExceeded ? 'text-emerald-700 dark:text-emerald-500' : 'text-rose-500'}`}>
                      No quantity exceeded
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── STICKY FOOTER ACTION BAR ── */}
            <div className={`fixed bottom-0 right-0 left-0 md:left-64 border-t px-6 py-4 flex items-center justify-end z-30 shadow-2xl transition-colors duration-300 ${
              isDark ? 'bg-slate-900 border-slate-800/80 text-white' : 'bg-white/95 border-gray-150 backdrop-blur'
            }`}>
              <button
                onClick={handleCreateTransfer}
                disabled={!isFormValid || creating}
                className={`flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider text-white shadow-lg transition-all ${
                  isFormValid && !creating
                    ? 'opacity-100 cursor-pointer hover:opacity-90 hover:shadow-emerald-500/20'
                    : 'opacity-40 cursor-not-allowed'
                }`}
                style={{ backgroundColor: (isFormValid && !creating) ? currentTheme.color : '#9ca3af' }}
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {creating ? 'Creating...' : 'Create Transfer'}
              </button>
            </div>
          </div>
        </>
      )}

      {viewMode === 'receive' && (
        <>
          {/* ── RECEIVE TRANSFER VIEW ── */}
          <div className="flex items-center gap-3 mb-6 animate-fade-in">
            <button
              onClick={() => setViewMode('list')}
              className={`w-9 h-9 rounded-full flex items-center justify-center border hover:opacity-80 transition-all cursor-pointer ${
                isDark ? 'border-slate-800 bg-slate-900 text-emerald-400' : 'border-emerald-100 bg-[#fbfbf6] text-emerald-600'
              }`}
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
            </button>
            <div>
              <h1 className={`text-xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Receive Transfer
              </h1>
              {selectedTransfer && (
                <p className={`text-[10px] font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'} mt-0.5`}>
                  #{selectedTransfer.transferNumber || selectedTransfer.id}
                </p>
              )}
            </div>
          </div>

          {!selectedTransfer ? (
            /* ── PENDING QUEUE PICKER ── */
            <div className={`border rounded-2xl p-6 transition-all ${
              isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
            }`}>
              <h2 className={`text-xs font-black uppercase tracking-wider mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Pick a transfer to receive
              </h2>
              {pendingQueueLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mr-2" />
                  <span className="text-gray-400 text-sm">Loading in-transit transfers...</span>
                </div>
              ) : pendingQueue.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No transfers currently in transit.</p>
              ) : (
                <div className="space-y-2">
                  {pendingQueue.map(t => (
                    <button
                      key={t.id}
                      onClick={() => selectTransferToReceive(mapTransfer(t))}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all cursor-pointer hover:border-emerald-400 ${
                        isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div>
                        <p className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {t.transferNumber || t.referenceId || `TR-${t.id}`}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {(t.sourceWarehouseName || '—')} → {(t.destinationWarehouseName || '—')}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 animate-fade-in">
            {/* Left 3 Columns */}
            <div className="xl:col-span-3 space-y-6">
              {/* Card 1: Source & Destination Summary */}
              <div className={`border rounded-2xl p-6 transition-all ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-[#fbfbf6] border-emerald-100/50 shadow-sm'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 divide-y md:divide-y-0 md:divide-x divide-gray-200/60 dark:divide-slate-800/60">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black uppercase tracking-wider text-gray-400">SOURCE WAREHOUSE</span>
                    <h3 className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {selectedTransfer.sourceWarehouseName || '—'}
                    </h3>
                  </div>
                  <div className="space-y-1 md:pl-6 pt-4 md:pt-0">
                    <span className="text-[9px] font-black uppercase tracking-wider text-gray-400">DESTINATION</span>
                    <h3 className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {selectedTransfer.destinationWarehouseName || '—'}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Card 2: Items to Verify */}
              <div className={`border rounded-2xl p-6 transition-all ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-xs font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Items to Verify
                  </h2>
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    {receiveItems.length} Items Total
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[700px]">
                    <thead>
                      <tr className={`border-b text-[10px] font-black uppercase tracking-wider text-[#5c8a36] ${
                        isDark ? 'border-slate-800' : 'border-gray-150'
                      }`}>
                        <th className="pb-3 pl-2">Product Name</th>
                        <th className="pb-3 text-center w-36">Expected Qty</th>
                        <th className="pb-3 text-center w-36">Received Qty</th>
                        <th className="pb-3 text-center w-36">Batch No</th>
                        <th className="pb-3 text-center w-24">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40">
                      {receiveItems.map((item) => {
                        const isDiscrepant = item.receivedQty !== item.expectedQty;
                        return (
                          <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/10">
                            <td className="py-4 pr-3 pl-2 flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Leaf className="w-4 h-4" />
                              </div>
                              <div>
                                <h4 className={`text-xs font-black ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{item.name}</h4>
                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">{item.sku}</p>
                              </div>
                            </td>
                            <td className="py-4 text-center">
                              <span className={`text-xs font-black ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                {item.expectedQty} Units
                              </span>
                            </td>
                            <td className="py-4">
                              <div className="flex justify-center">
                                <input
                                  type="number"
                                  value={item.receivedQty === 0 ? '' : item.receivedQty}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                                    handleReceiveQtyChange(item.id, val);
                                  }}
                                  className={`w-24 px-3 py-1.5 rounded-full border text-xs text-center font-bold outline-none focus:ring-1 transition-all ${
                                    isDiscrepant
                                      ? 'border-rose-500 text-rose-600 bg-rose-50/30 focus:ring-rose-500'
                                      : isDark
                                        ? 'bg-slate-900 border-slate-800 text-white focus:ring-emerald-500'
                                        : 'bg-[#f4f4f2] border-gray-200 text-gray-800 focus:ring-emerald-500'
                                  }`}
                                />
                              </div>
                            </td>
                            <td className="py-4 text-center">
                              <span className="text-[10px] font-bold text-gray-500 font-mono">{item.batchNo}</span>
                            </td>
                            <td className="py-4 text-center">
                              <div className="flex justify-center">
                                {isDiscrepant ? (
                                  <span className="w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                  </span>
                                ) : (
                                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end mt-4 pt-4 border-t border-gray-150 dark:border-slate-800/40">
                  <span className={`text-xs font-mono font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-700'}`}>
                    Total Received: {totalReceived} / {totalExpected} Units
                  </span>
                </div>
              </div>

              {/* Card 3: Confirmation */}
              <div className={`border rounded-2xl p-5 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                isDark ? 'bg-slate-900/20 border-slate-800/50' : 'bg-[#eef5ee]/50 border-emerald-100/50'
              }`}>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 font-bold">
                  Stock will be instantly updated in the destination warehouse after confirmation.
                </p>
                <button
                  onClick={handleConfirmReceive}
                  disabled={confirmingReceive}
                  className="flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider text-white shadow-lg cursor-pointer transition-all hover:opacity-90 whitespace-nowrap bg-[#55825a] disabled:opacity-60"
                >
                  {confirmingReceive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rotate-45 mr-1" />}
                  {confirmingReceive ? 'Confirming...' : 'Confirm and mark Received'}
                </button>
              </div>
            </div>

            {/* Right Column */}
            <div className="xl:col-span-1 space-y-6">
              {hasVariance && (
                <div className="border border-rose-100 bg-[#fdf5f5] rounded-2xl p-5 border-l-4 border-l-rose-500 shadow-sm animate-fade-in">
                  <div className="flex items-start gap-3 text-rose-600">
                    <AlertTriangle className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-rose-700">Variance Detected</h4>
                      <p className="text-[10px] text-rose-600 font-semibold mt-1 leading-relaxed">
                        Found a discrepancy of {totalReceived - totalExpected} units in {discrepantItems.map(i => i.name).join(', ')}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className={`border rounded-2xl p-6 transition-all ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <h3 className={`text-xs font-black uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  <FileText className="w-4 h-4 text-gray-400" />
                  Receiving Notes
                </h3>
                <textarea
                  value={receivingNotes}
                  onChange={(e) => setReceivingNotes(e.target.value)}
                  placeholder="Add comments about shipment condition, pallet quality, or any breakage observed during offloading..."
                  className={`w-full h-32 p-3 text-xs border rounded-xl outline-none focus:border-emerald-500 transition-all resize-none ${
                    isDark
                      ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                      : 'bg-[#f8f8f6] border-gray-205 text-gray-800 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>
          </div>
          )}
        </>
      )}

      {viewMode === 'document' && (
        <>
          {/* Style block for print styling */}
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * {
                visibility: hidden;
              }
              #printable-document, #printable-document * {
                visibility: visible;
              }
              #printable-document {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                border: none !important;
                box-shadow: none !important;
                padding: 0 !important;
                margin: 0 !important;
              }
            }
          `}} />

          {/* ── TOP NAV SECTION ── */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setViewMode('list')}
                className={`w-9 h-9 rounded-full flex items-center justify-center border hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer ${
                  isDark ? 'border-slate-800 text-gray-300' : 'border-gray-200 text-gray-700'
                }`}
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
              <h1 className={`text-xl font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {docType === 'challan' ? 'Delivery Challan Detail' : docType === 'invoice' ? 'Invoice Detail' : 'E-way Bill Detail'}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Download real backend-generated PDF */}
              <button
                onClick={() => selectedDocTransfer && handleDownloadDoc(selectedDocTransfer.id, docType)}
                disabled={docDownloading || !selectedDocTransfer}
                className="p-2.5 rounded-full border bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 transition-all cursor-pointer shadow-sm disabled:opacity-50"
                title="Download PDF"
              >
                {docDownloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              </button>

              <button
                onClick={() => window.print()}
                className="p-2.5 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100 transition-all cursor-pointer shadow-sm"
                title="Print"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            </div>
          </div>

          {docLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mr-2" />
              <span className="text-gray-400 text-sm">Loading document details...</span>
            </div>
          ) : selectedDocTransfer ? (
            docType === 'eway' ? (
              /* E-way Bill layout matching user reference image */
              <div
                id="printable-document"
                className="bg-white text-gray-900 rounded-3xl border border-gray-150 shadow-sm max-w-4xl mx-auto overflow-hidden font-sans"
              >
                {/* E-way Bill Header Block (Dark Olive Green) */}
                <div className="bg-[#4d6b32] p-6 text-white flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-[9px] text-white/70 font-bold uppercase tracking-wider block">E-WAY BILL NUMBER</span>
                      <span className="text-xl font-extrabold tracking-wide">
                        EWB-{selectedDocTransfer.referenceId?.replace('TR-', '') || '2627-098123'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-6 text-right">
                    <div>
                      <span className="text-[9px] text-white/70 font-bold uppercase tracking-wider block">GENERATED DATE</span>
                      <span className="text-xs font-bold">{formatDate(selectedDocTransfer.transferDate || selectedDocTransfer.date)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-white/70 font-bold uppercase tracking-wider block">VALIDITY DATE</span>
                      <span className="text-xs font-bold">
                        {(() => {
                          const d = new Date(selectedDocTransfer.transferDate || selectedDocTransfer.date || new Date());
                          d.setDate(d.getDate() + 1);
                          return formatDate(d);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* PART A: TRANSACTION DETAILS */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-gray-650 text-white font-extrabold text-xs flex items-center justify-center">A</span>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#4d6b32]">PART A: TRANSACTION DETAILS</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CONSIGNOR (FROM)</span>
                        <h4 className="text-xs font-extrabold text-gray-905">Aarogya India Pvt. Ltd.</h4>
                        <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                          Plot No. 42, Sector 62, Noida, Uttar Pradesh - 201309
                        </p>
                        <div className="border-t border-gray-100 my-2" />
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-400 font-bold">GSTIN</span>
                          <span className="font-bold text-gray-700">09AAACA1234A1Z5</span>
                        </div>
                      </div>
                      <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CONSIGNEE (TO)</span>
                        <h4 className="text-xs font-extrabold text-gray-905">
                          {selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination || selectedDocTransfer.transferToName || 'Bulandshahr Hospital'}
                        </h4>
                        <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                          {selectedDocTransfer.destinationAddress || 'Medical Enclave, Civil Lines, Bulandshahr, Uttar Pradesh - 203001'}
                        </p>
                        <div className="border-t border-gray-100 my-2" />
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-400 font-bold">GSTIN</span>
                          <span className="font-bold text-gray-700">09BBBCB5678B2Z1</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PART B: TRANSPORT DETAILS */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-gray-650 text-white font-extrabold text-xs flex items-center justify-center">B</span>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#4d6b32]">PART B: TRANSPORT DETAILS</h3>
                    </div>

                    <div className="border border-gray-150 rounded-2xl p-4 space-y-4">
                      <div className="grid grid-cols-4 gap-4 text-[10px]">
                        <div>
                          <span className="text-gray-400 font-bold uppercase block mb-0.5">TRANSPORTER NAME</span>
                          <span className="font-extrabold text-gray-900">{selectedDocTransfer.transporterName || 'Delhivery Express'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 font-bold uppercase block mb-0.5">TRANSPORTER ID</span>
                          <span className="font-extrabold text-gray-900">T-ID98231</span>
                        </div>
                        <div>
                          <span className="text-gray-400 font-bold uppercase block mb-0.5">MODE</span>
                          <span className="font-extrabold text-gray-900 flex items-center gap-1">Road</span>
                        </div>
                        <div>
                          <span className="text-gray-400 font-bold uppercase block mb-0.5">DISTANCE</span>
                          <span className="font-extrabold text-gray-900">92 km</span>
                        </div>
                      </div>

                      <div className="inline-flex items-center gap-3 px-4 py-2 border border-emerald-100 bg-emerald-50/20 rounded-xl">
                        <span className="text-[8px] font-black text-emerald-800 uppercase tracking-widest block">VEHICLE NUMBER</span>
                        <div className="flex items-center gap-1.5 text-xs font-extrabold text-emerald-900">
                          <span>🚚</span>
                          <span>{selectedDocTransfer.vehicleNumber || 'UP16AB1234'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ITEMIZED GOODS */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-black uppercase tracking-wider text-[#4d6b32]">ITEMIZED GOODS</h3>

                    <div className="border border-gray-150 rounded-2xl overflow-hidden">
                      <table className="w-full text-left border-collapse text-[11px]">
                        <thead>
                          <tr className="bg-gray-100 border-b border-gray-150 text-gray-500 font-bold">
                            <th className="py-2.5 pl-4 text-center w-12">S.NO</th>
                            <th className="py-2.5 pl-2">PRODUCT NAME</th>
                            <th className="py-2.5 text-center w-24">HSN CODE</th>
                            <th className="py-2.5 text-center w-28">QUANTITY</th>
                            <th className="py-2.5 text-center w-28">VALUE (₹)</th>
                            <th className="py-2.5 text-center w-36 pr-4">TAX RATES</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-150 text-gray-700">
                          {(() => {
                            const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                            if (itemsList.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="6" className="py-8 text-center text-gray-400">
                                    No goods found in this transfer
                                  </td>
                                </tr>
                              );
                            }

                            const sameState = getWarehouseState(selectedDocTransfer.sourceWarehouseName || selectedDocTransfer.source) ===
                                              getWarehouseState(selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination);

                            return itemsList.map((item, index) => {
                              const qty = item.transferQty || item.quantity || item.expectedQty || 0;
                              const price = getItemPrice(item.productName || item.name);
                              const val = qty * price;
                              const hsn = getMockHsn(item.productName || item.name).substring(0, 4);

                              return (
                                <tr key={item.id || index}>
                                  <td className="py-3 text-center">{String(index + 1).padStart(2, '0')}</td>
                                  <td className="py-3 pl-2 font-bold text-gray-900">{item.productName || item.name || '—'}</td>
                                  <td className="py-3 text-center font-mono">{hsn}</td>
                                  <td className="py-3 text-center font-bold text-gray-900">{qty.toLocaleString()} Units</td>
                                  <td className="py-3 text-center font-bold text-gray-900">{formatRupees(val)}</td>
                                  <td className="py-3 text-center pr-4">
                                    {sameState ? (
                                      <div className="flex justify-center gap-1.5">
                                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-650 text-[9px] font-bold">CGST 6%</span>
                                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-650 text-[9px] font-bold">SGST 6%</span>
                                      </div>
                                    ) : (
                                      <div className="flex justify-center">
                                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-650 text-[9px] font-bold">IGST 12%</span>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                          <tr className="bg-gray-50 border-t border-gray-150 font-bold text-gray-900">
                            <td colSpan="4" className="py-3 pl-4 text-right pr-6 uppercase tracking-wider text-[10px] text-gray-400 font-black font-sans">
                              Total Taxable Value
                            </td>
                            <td className="py-3 text-center font-extrabold text-xs text-[#4d6b32]">
                              {(() => {
                                const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                                const totalVal = itemsList.reduce((sum, i) => {
                                  const qty = i.transferQty || i.quantity || i.expectedQty || 0;
                                  const price = getItemPrice(i.productName || i.name);
                                  return sum + (qty * price);
                                }, 0);
                                return formatRupees(totalVal);
                              })()}
                            </td>
                            <td className="py-3 pr-4"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Footer verification info */}
                  <div className="grid grid-cols-3 gap-6 pt-4 border-t border-gray-100">
                    <div className="col-span-2 flex items-center gap-4">
                      <div className="w-16 h-16 border border-emerald-200 bg-emerald-50/10 p-1 rounded-xl flex items-center justify-center flex-shrink-0">
                        {/* Green Mock QR Code */}
                        <svg className="w-full h-full text-emerald-800" viewBox="0 0 100 100" fill="currentColor">
                          <rect x="10" y="10" width="20" height="20" />
                          <rect x="15" y="15" width="10" height="10" fill="white" />
                          <rect x="70" y="10" width="20" height="20" />
                          <rect x="75" y="15" width="10" height="10" fill="white" />
                          <rect x="10" y="70" width="20" height="20" />
                          <rect x="15" y="75" width="10" height="10" fill="white" />
                          <rect x="35" y="20" width="10" height="10" />
                          <rect x="50" y="35" width="15" height="15" />
                          <rect x="25" y="45" width="15" height="10" />
                          <rect x="40" y="60" width="20" height="20" />
                          <rect x="70" y="45" width="15" height="15" />
                          <rect x="75" y="70" width="15" height="15" />
                        </svg>
                      </div>
                      <div>
                        <span className="text-[9px] font-black text-gray-800 uppercase block tracking-wider mb-0.5">Verification QR Code</span>
                        <p className="text-[8px] text-gray-400 font-medium leading-normal max-w-[280px]">
                          Scan to verify the authenticity of this E-Way Bill on the GST Portal.
                        </p>
                      </div>
                    </div>

                    <div className="text-right flex flex-col justify-end">
                      <span className="text-[8px] font-mono text-emerald-800 block leading-none font-bold uppercase tracking-wider mb-1">
                        ✓ Digital Signature Verified
                      </span>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block">AUTHORIZED SIGNATORY</span>
                      <p className="text-[9px] font-extrabold text-gray-850 mt-0.5 leading-none">Aarogya India Logistics Dept.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : docType === 'invoice' ? (
              /* Tax Invoice layout matching image/professional style */
              <div
                id="printable-document"
                className="bg-white text-gray-900 rounded-3xl border border-gray-150 shadow-sm max-w-4xl mx-auto overflow-hidden font-sans p-8 space-y-6"
              >
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h2 className="text-xl font-extrabold text-[#3a5a40] tracking-wide">
                      Aarogya India Pvt. Ltd.
                    </h2>
                    <p className="text-[10px] text-gray-500 leading-normal max-w-[280px]">
                      E-23, Sector-63, Noida - 201301, Uttar Pradesh
                      <br />
                      GSTIN: 09AAECL9733A1ZJ | CIN: U24233UP2019PTC118125
                    </p>
                    <p className="text-[10px] text-gray-500">
                      📞 +91-7065088499 | ✉️ info@aarogyaindia25.com
                    </p>
                  </div>

                  <div className="border border-[#3a5a40] bg-[#3a5a40]/5 text-[#3a5a40] font-extrabold text-[12px] uppercase tracking-widest px-4 py-2.5 rounded-xl">
                    TAX INVOICE
                  </div>
                </div>

                <div className="border-t border-gray-150 my-4" />

                {/* Metadata Grid */}
                <div className="grid grid-cols-3 border border-gray-150 rounded-2xl overflow-hidden bg-gray-50/50">
                  <div className="p-4 border-r border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">INVOICE NUMBER</span>
                    <span className="text-xs font-bold text-gray-800">
                      INV-{selectedDocTransfer.referenceId?.replace('TR-', '') || selectedDocTransfer.id}
                    </span>
                  </div>
                  <div className="p-4 border-r border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">INVOICE DATE</span>
                    <span className="text-xs font-bold text-gray-800">
                      {formatDate(selectedDocTransfer.transferDate || selectedDocTransfer.date)}
                    </span>
                  </div>
                  <div className="p-4 border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">DUE DATE</span>
                    <span className="text-xs font-bold text-gray-855">
                      {formatDate(selectedDocTransfer.transferDate || selectedDocTransfer.date)}
                    </span>
                  </div>
                  <div className="p-4 border-r border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">VEHICLE NUMBER</span>
                    <span className="text-xs font-bold text-gray-800 uppercase">{selectedDocTransfer.vehicleNumber || 'UP16AB1234'}</span>
                  </div>
                  <div className="p-4 border-r border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">TRANSPORTER</span>
                    <span className="text-xs font-bold text-gray-800 uppercase">{selectedDocTransfer.transporterName || 'Delhivery Express'}</span>
                  </div>
                  <div className="p-4">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">MODE OF TRANSPORT</span>
                    <span className="text-xs font-bold text-gray-800">Road</span>
                  </div>
                </div>

                {/* Billed & Shipped To */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs">📍</span>
                      <span className="text-[9px] font-black text-[#3a5a40] uppercase tracking-wider">BILLED TO</span>
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-855">
                      {selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination || selectedDocTransfer.transferToName || 'Bulandshahr Hospital'}
                    </h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                      {selectedDocTransfer.destinationAddress || 'Civil Lines, Near DM Office, Bulandshahr, Uttar Pradesh - 203001'}
                    </p>
                  </div>
                  <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs">📍</span>
                      <span className="text-[9px] font-black text-[#3a5a40] uppercase tracking-wider">SHIPPED TO</span>
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-855">
                      {selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination || selectedDocTransfer.transferToName || 'Bulandshahr Hospital'}
                    </h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                      {selectedDocTransfer.destinationAddress || 'Civil Lines, Near DM Office, Bulandshahr, Uttar Pradesh - 203001'}
                    </p>
                  </div>
                </div>

                {/* Items table */}
                <div className="border border-gray-150 rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse text-[11px]">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-150 text-gray-500 font-bold">
                        <th className="py-2.5 pl-4 text-center w-12">S. No</th>
                        <th className="py-2.5 pl-2">Product Description</th>
                        <th className="py-2.5 text-center w-20">HSN</th>
                        <th className="py-2.5 text-center w-16">Qty</th>
                        <th className="py-2.5 text-center w-24">Unit Price</th>
                        <th className="py-2.5 text-center w-28">GST Rate</th>
                        <th className="py-2.5 text-center w-28 pr-4">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150 text-gray-700">
                      {(() => {
                        const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                        if (itemsList.length === 0) {
                          return (
                            <tr>
                              <td colSpan="7" className="py-8 text-center text-gray-400">
                                No products listed in this invoice
                              </td>
                            </tr>
                          );
                        }

                        const sameState = getWarehouseState(selectedDocTransfer.sourceWarehouseName || selectedDocTransfer.source) ===
                                          getWarehouseState(selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination);

                        return itemsList.map((item, index) => {
                          const qty = item.transferQty || item.quantity || item.expectedQty || 0;
                          const price = getItemPrice(item.productName || item.name);
                          const totalAmount = qty * price;
                          const hsn = getMockHsn(item.productName || item.name).substring(0, 4);

                          return (
                            <tr key={item.id || index}>
                              <td className="py-3 text-center">{index + 1}</td>
                              <td className="py-3 pl-2 font-bold text-gray-900">{item.productName || item.name || '—'}</td>
                              <td className="py-3 text-center font-mono">{hsn}</td>
                              <td className="py-3 text-center font-bold text-gray-900">{qty}</td>
                              <td className="py-3 text-center">{formatRupees(price)}</td>
                              <td className="py-3 text-center">
                                {sameState ? 'CGST 6% + SGST 6%' : 'IGST 12%'}
                              </td>
                              <td className="py-3 text-center font-bold text-gray-900 pr-4">{formatRupees(totalAmount)}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Summary Block */}
                <div className="flex justify-end pt-4">
                  <div className="w-80 border border-gray-150 rounded-2xl p-4 bg-gray-50/20 text-[11px] space-y-2 font-bold text-gray-700">
                    {(() => {
                      const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                      const taxableVal = itemsList.reduce((sum, i) => {
                        const qty = i.transferQty || i.quantity || i.expectedQty || 0;
                        const price = getItemPrice(i.productName || i.name);
                        return sum + (qty * price);
                      }, 0);

                      const sameState = getWarehouseState(selectedDocTransfer.sourceWarehouseName || selectedDocTransfer.source) ===
                                        getWarehouseState(selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination);

                      const gstRate = 0.12; // 12% GST
                      const gstAmount = taxableVal * gstRate;
                      const grandTotal = taxableVal + gstAmount;

                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-400 font-medium">Taxable Value</span>
                            <span>{formatRupees(taxableVal)}</span>
                          </div>
                          {sameState ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-400 font-medium">CGST (6%)</span>
                                <span>{formatRupees(gstAmount / 2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400 font-medium">SGST (6%)</span>
                                <span>{formatRupees(gstAmount / 2)}</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between">
                              <span className="text-gray-400 font-medium">IGST (12%)</span>
                              <span>{formatRupees(gstAmount)}</span>
                            </div>
                          )}
                          <div className="border-t border-gray-150 my-1 pt-1 flex justify-between text-[#3a5a40] font-black text-xs">
                            <span>Grand Total</span>
                            <span>{formatRupees(grandTotal)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Bottom Signatures */}
                <div className="flex justify-between items-end pt-12 text-[10px] text-gray-500">
                  <div className="text-left max-w-xs space-y-1">
                    <span className="font-bold text-gray-700 block">Terms & Conditions</span>
                    <p className="text-[8px] text-gray-400 leading-normal">
                      1. Goods once sold will not be taken back.
                      <br />
                      2. Subject to Noida jurisdiction only.
                    </p>
                  </div>

                  <div className="text-center space-y-4">
                    <div className="h-10 flex items-end justify-center">
                      <span className="font-serif italic text-xl text-emerald-800 font-bold opacity-80" style={{ fontFamily: 'cursive' }}>
                        Aarogya India
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-1 w-40">
                      <span className="font-bold uppercase tracking-wider text-gray-400">AUTHORIZED SIGNATORY</span>
                      <p className="text-[8px] text-gray-400 font-medium mt-0.5">For Aarogya India Pvt. Ltd.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Delivery Challan layout */
              <div
                id="printable-document"
                className="bg-white text-gray-900 rounded-3xl border border-gray-150 p-8 shadow-sm max-w-4xl mx-auto space-y-6"
              >
                {/* Aarogya India Header */}
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h2 className="text-xl font-extrabold text-[#3a5a40] tracking-wide">
                      Aarogya India Pvt. Ltd.
                    </h2>
                    <p className="text-[10px] text-gray-500 leading-normal max-w-[280px]">
                      E-23, Sector-63, Noida - 201301, Uttar Pradesh
                      <br />
                      GSTIN: 09AAECL9733A1ZJ | CIN: U24233UP2019PTC118125
                    </p>
                    <p className="text-[10px] text-gray-500">
                      📞 +91-7065088499 | ✉️ info@aarogyaindia25.com
                    </p>
                  </div>

                  <div className="border border-emerald-800 bg-emerald-50/20 text-emerald-800 font-extrabold text-[11px] uppercase tracking-widest px-4 py-2 rounded-xl">
                    {docType === 'challan' ? 'Delivery Challan' : docType === 'invoice' ? 'Tax Invoice' : 'E-way Bill'}
                  </div>
                </div>

                <div className="border-t border-gray-150 my-4" />

                {/* Document Meta Information */}
                <div className="grid grid-cols-3 border border-gray-150 rounded-2xl overflow-hidden bg-gray-50/50">
                  <div className="p-4 border-r border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">
                      {docType === 'challan' ? 'DC NUMBER' : docType === 'invoice' ? 'INVOICE NO.' : 'E-WAY BILL NO.'}
                    </span>
                    <span className="text-xs font-bold text-gray-800">
                      {docType === 'challan'
                        ? (selectedDocTransfer.referenceId || selectedDocTransfer.transferId || 'DC-26-27-000221')
                        : docType === 'invoice'
                          ? `INV-${selectedDocTransfer.id}`
                          : (selectedDocTransfer.referenceId || '9812 3456 7890')}
                    </span>
                  </div>
                  <div className="p-4 border-r border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">
                      {docType === 'challan' ? 'DC DATE' : docType === 'invoice' ? 'INVOICE DATE' : 'GENERATED DATE'}
                    </span>
                    <span className="text-xs font-bold text-gray-800">
                      {formatDate(selectedDocTransfer.transferDate || selectedDocTransfer.date)}
                    </span>
                  </div>
                  <div className="p-4 border-b border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">TRANSPORT TYPE</span>
                    <span className="text-xs font-bold text-red-600 uppercase">Stock Transfer</span>
                  </div>
                  <div className="p-4 border-r border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">VEHICLE NUMBER</span>
                    <span className="text-xs font-bold text-gray-800 uppercase">{selectedDocTransfer.vehicleNumber || 'UP16AB1234'}</span>
                  </div>
                  <div className="p-4 border-r border-gray-150">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">TRANSPORTER</span>
                    <span className="text-xs font-bold text-gray-800 uppercase">{selectedDocTransfer.transporterName || 'Delhivery Express'}</span>
                  </div>
                  <div className="p-4">
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest block">E-WAY BILL NO.</span>
                    <span className="text-xs font-bold text-gray-800">{selectedDocTransfer.referenceId || '9812 3456 7890'}</span>
                  </div>
                </div>

                {/* From and To Addresses */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs">📍</span>
                      <span className="text-[9px] font-black text-emerald-800 uppercase tracking-wider">FROM WAREHOUSE</span>
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-850">
                      {selectedDocTransfer.sourceWarehouseName || selectedDocTransfer.source || 'Main Warehouse (HQ)'}
                    </h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                      {selectedDocTransfer.sourceAddress || 'E-23, Sector-63, Noida, Uttar Pradesh - 201301\nGSTIN: 09AAECL9733A1ZJ'}
                    </p>
                  </div>
                  <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs">📍</span>
                      <span className="text-[9px] font-black text-emerald-800 uppercase tracking-wider">TO WAREHOUSE</span>
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-850">
                      {selectedDocTransfer.destinationWarehouseName || selectedDocTransfer.destination || selectedDocTransfer.transferToName || 'Bulandshahr Hospital'}
                    </h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                      {selectedDocTransfer.destinationAddress || 'Civil Lines, Near DM Office, Bulandshahr, Uttar Pradesh - 203001\nContact: Mr. Sharma (+91-9988776655)'}
                    </p>
                  </div>
                </div>

                {/* Table of Items */}
                <div className="border border-gray-150 rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse text-[11px]">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-150 text-gray-500 font-bold">
                        <th className="py-2.5 pl-4 text-center w-12">S. No</th>
                        <th className="py-2.5 pl-2">Item Name</th>
                        <th className="py-2.5 text-center w-24">Batch No</th>
                        <th className="py-2.5 text-center w-20">Exp. Date</th>
                        <th className="py-2.5 text-center w-20">HSN</th>
                        <th className="py-2.5 text-center w-16">Qty</th>
                        <th className="py-2.5 text-center w-16 pr-4">Unit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150 text-gray-700">
                      {(() => {
                        const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                        if (itemsList.length === 0) {
                          return (
                            <tr>
                              <td colSpan="7" className="py-8 text-center text-gray-400">
                                No items found in this transfer
                              </td>
                            </tr>
                          );
                        }
                        return itemsList.map((item, index) => (
                          <tr key={item.id || index}>
                            <td className="py-3 text-center">{index + 1}</td>
                            <td className="py-3 pl-2 font-bold text-gray-900">{item.productName || item.name || '—'}</td>
                            <td className="py-3 text-center font-mono">{item.batchNumber || item.batchNo || '—'}</td>
                            <td className="py-3 text-center">{getMockExpiry(item.expiryDate)}</td>
                            <td className="py-3 text-center">{getMockHsn(item.productName || item.name)}</td>
                            <td className="py-3 text-center font-bold text-gray-900">{item.transferQty || item.quantity || item.expectedQty || 0}</td>
                            <td className="py-3 text-center pr-4">{getMockUnit(item.productName || item.name)}</td>
                          </tr>
                        ));
                      })()}
                      <tr className="bg-gray-50 border-t border-gray-150 font-bold text-gray-900">
                        <td colSpan="5" className="py-3 pl-4 text-right pr-6 uppercase tracking-wider text-[10px] text-gray-400 font-black font-sans">
                          Total Quantity Across All Items:
                        </td>
                        <td className="py-3 text-center font-extrabold text-xs">
                          {(() => {
                            const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                            return itemsList.reduce((sum, i) => sum + (i.transferQty || i.quantity || i.expectedQty || 0), 0);
                          })()}
                        </td>
                        <td className="py-3 pr-4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Bottom Section */}
                <div className="grid grid-cols-3 gap-6 pt-4">
                  <div className="col-span-2 border border-red-100 bg-red-50/20 rounded-2xl p-4 text-[10px] leading-relaxed text-red-700">
                    <span className="font-extrabold block uppercase tracking-wider mb-1">Remarks / Instructions</span>
                    ⚠️ {selectedDocTransfer.notes || 'Handle with Care. Fragile medical glassware inside.'}
                  </div>

                  <div className="border border-gray-150 rounded-2xl p-4 bg-gray-50/20 text-[10px] space-y-1.5 font-bold text-gray-700">
                    <div className="flex justify-between">
                      <span className="text-gray-400 font-medium">Total Cases</span>
                      <span>
                        {(() => {
                          const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                          const totalQty = itemsList.reduce((sum, i) => sum + (i.transferQty || i.quantity || i.expectedQty || 0), 0);
                          return String(Math.ceil(totalQty / 12)).padStart(2, '0');
                        })()} Cases
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 font-medium">Gross Weight</span>
                      <span>
                        {(() => {
                          const itemsList = selectedDocTransfer.items || selectedDocTransfer.transferItems || [];
                          const totalQty = itemsList.reduce((sum, i) => sum + (i.transferQty || i.quantity || i.expectedQty || 0), 0);
                          return (totalQty * 0.5 + (totalQty > 0 ? 0.5 : 0)).toFixed(2);
                        })()} Kg
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 font-medium">Mode of Transport</span>
                      <span>Road</span>
                    </div>
                  </div>
                </div>

                {/* Bottom Signatures */}
                <div className="flex justify-between items-end pt-12 text-[10px] text-gray-500">
                  <div className="text-center space-y-4">
                    <div className="h-10 flex items-end justify-center">
                      <span className="font-serif italic text-xl text-emerald-800 font-bold opacity-80" style={{ fontFamily: 'cursive' }}>
                        Aarogya India
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-1 w-40">
                      <span className="font-bold uppercase tracking-wider text-gray-400">AUTHORIZED SIGNATORY</span>
                      <p className="text-[8px] text-gray-400 font-medium mt-0.5">For Aarogya India Pvt. Ltd.</p>
                    </div>
                  </div>

                  <div className="text-center space-y-4">
                    <div className="h-10 flex items-end justify-center">
                      <span className="font-serif italic text-xl text-blue-800 font-bold opacity-80" style={{ fontFamily: 'cursive' }}>
                        Hospital Staff
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-1 w-40">
                      <span className="font-bold uppercase tracking-wider text-gray-400">RECEIVER SIGNATURE</span>
                      <p className="text-[8px] text-gray-400 font-medium mt-0.5">Hospital Staff</p>
                    </div>
                  </div>
                </div>

                {/* Footer Notice */}
                <div className="text-center text-[8px] text-gray-400 pt-8 border-t border-gray-100">
                  This is a computer generated document and does not require a physical signature if verified via QR.
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-12 text-gray-400">
              No document details loaded.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StockTransfer;